// src/screens/auth/LoginScreen.jsx
import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import AuthLayout from "../../components/auth/AuthLayout";
import InputField from "../../components/common/InputField";
import Button from "../../components/common/Button";
import SocialDivider from "../../components/auth/SocialDivider";
import { signInWithEmailAndPassword } from "firebase/auth";
import { auth } from "../../firebase";

const MailIcon = ({ size = 17, strokeWidth = 2 }) => (
  <svg width={size} height={size} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={strokeWidth}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
  </svg>
);
const LockIcon = ({ size = 17, strokeWidth = 2 }) => (
  <svg width={size} height={size} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={strokeWidth}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
  </svg>
);

const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
  </svg>
);

const LinkedInIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="#0A66C2">
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
  </svg>
);

const LoginScreen = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);

  const validate = () => {
    const errs = {};
    if (!form.email) errs.email = "Email is required";
    else if (!/\S+@\S+\.\S+/.test(form.email)) errs.email = "Enter a valid email address";
    if (!form.password) errs.password = "Password is required";
    else if (form.password.length < 6) errs.password = "Password must be at least 6 characters";
    return errs;
  };

  const handleChange = (field) => (e) => {
    setForm((p) => ({ ...p, [field]: e.target.value }));
    if (errors[field]) setErrors((p) => ({ ...p, [field]: "" }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setLoading(true);
    try {
      await signInWithEmailAndPassword(auth, form.email, form.password);
      alert("Login successful!");
      navigate("/dashboard");
    } catch (error) {
      console.error("Login error:", error);
      setErrors((prev) => ({
        ...prev,
        password: "Login failed. Please check your credentials.",
      }));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout
      title="Welcome back"
      subtitle="Sign in to your Quinternship account"
      illustrationTitle={"Unlock Your\nPotential"}
      illustrationSubtitle="Sign in to access your dashboard, track your applications, and discover new internship opportunities."
    >
      <form id="login-form" onSubmit={handleSubmit} noValidate className="space-y-5">
        {/* Email */}
        <InputField
          id="login-email"
          label="Email Address"
          type="email"
          placeholder="you@example.com"
          value={form.email}
          onChange={handleChange("email")}
          icon={MailIcon}
          error={errors.email}
          required
          autoComplete="email"
        />

        {/* Password */}
        <div>
          <InputField
            id="login-password"
            label="Password"
            type="password"
            placeholder="Enter your password"
            value={form.password}
            onChange={handleChange("password")}
            icon={LockIcon}
            error={errors.password}
            required
            autoComplete="current-password"
          />
          <div className="flex justify-end mt-2">
            <Link
              to="/forgot-password"
              id="forgot-password-link"
              className="text-xs font-semibold text-primary-500 hover:text-primary-700 transition-colors duration-200"
            >
              Forgot password?
            </Link>
          </div>
        </div>

        {/* Remember me */}
        <label className="flex items-center gap-2.5 cursor-pointer select-none group" htmlFor="remember-me">
          <div className="relative">
            <input
              id="remember-me"
              type="checkbox"
              className="sr-only"
              checked={rememberMe}
              onChange={() => setRememberMe((v) => !v)}
            />
            <div
              className={[
                "w-4.5 h-4.5 w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all duration-200",
                rememberMe
                  ? "bg-primary-500 border-primary-500"
                  : "border-slate-300 bg-white/60 group-hover:border-primary-300",
              ].join(" ")}
            >
              {rememberMe && (
                <svg width="10" height="8" fill="none" viewBox="0 0 10 8">
                  <path d="M1 4l2.5 2.5L9 1" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              )}
            </div>
          </div>
          <span className="text-sm font-medium text-slate-600">Remember me for 30 days</span>
        </label>

        {/* Submit */}
        <Button
          id="login-submit-btn"
          type="submit"
          variant="primary"
          size="lg"
          fullWidth
          loading={loading}
        >
          {loading ? "Signing in..." : "Sign In"}
        </Button>

        <SocialDivider />

        {/* Social buttons */}
        <div className="grid grid-cols-2 gap-3">
          <button
            id="google-login-btn"
            type="button"
            className="flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl border border-white/70 bg-white/60 backdrop-blur-sm hover:bg-white/80 hover:border-slate-200 transition-all duration-300 text-sm font-semibold text-slate-700 shadow-sm"
          >
            <GoogleIcon />
            Google
          </button>
          <button
            id="linkedin-login-btn"
            type="button"
            className="flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl border border-white/70 bg-white/60 backdrop-blur-sm hover:bg-white/80 hover:border-slate-200 transition-all duration-300 text-sm font-semibold text-slate-700 shadow-sm"
          >
            <LinkedInIcon />
            LinkedIn
          </button>
        </div>

        {/* Register link */}
        <p className="text-center text-sm text-slate-500 font-medium pt-1">
          Don&apos;t have an account?{" "}
          <Link
            to="/register"
            id="go-to-register-link"
            className="text-primary-500 font-bold hover:text-primary-700 transition-colors duration-200"
          >
            Create one free
          </Link>
        </p>

        {/* Razorpay Verification Notice */}
        <div style={{ marginTop: "24px", padding: "12px", background: "rgba(139,92,246,0.1)", borderRadius: "12px", border: "1px solid rgba(139,92,246,0.2)", textAlign: "center" }}>
          <p style={{ fontSize: "12px", color: "#6B7280", margin: 0, lineHeight: 1.5 }}>
            <strong>Razorpay Verification Notice:</strong> This is our temporary official web application. We are currently getting a new web address for payment processing. 
            <br />Official Website: <a href="https://dhakshkalleshtechsolutions.web.app/" target="_blank" rel="noreferrer" style={{ color: "#7C3AED", fontWeight: 700, textDecoration: "underline" }}>https://dhakshkalleshtechsolutions.web.app/</a>
          </p>
        </div>
      </form>
    </AuthLayout>
  );
};

export default LoginScreen;
